import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../../ui-components/confirmation-modal/confirmationModal";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import FreightItemCard from "../../../common/freight-item/freightItemCard";
import PickupItemForm from "./pickupItemForm";
import {
  addItem,
  clearDeletedItems,
  clearRequestError,
  deleteItem,
  pickupSelector,
  updateItem
} from "../../../../slices/pickup/pickupSlice";
import CommonState from "../../../../slices/common/CommonState";
import { commonSelector } from "../../../../slices/common/commonSlice";
import "../../../../sass/blocks.scss";
import XGSCheckbox from "../../../../ui-components/xgs-checkbox/xgsCheckbox";

interface PickupItemsStepProps {
  next?: () => void;
  previous?: () => void;
  onSubmit: () => void;
  loadingOnSubmit: boolean;
}

const PickupItemsStep: React.FC<PickupItemsStepProps> = ({ next, previous, onSubmit, loadingOnSubmit }) => {
  const dispatch = useDispatch();

  const pickupState = useSelector(pickupSelector);
  const commonState: CommonState = useSelector(commonSelector);
  const [showAddItemForm, setAddItemForm] = useState<boolean>(false);
  const [removeItemIndex, setRemoveItemIndex] = useState<number | undefined>(undefined);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState<boolean>(false);
  const [checkConfirm, setCheckConfirm] = useState<boolean>(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState<boolean>(false);
  const [editItemIndex, setEditItemIndex] = useState<number | undefined>(undefined);
  const [copyItemIndex, setCopyItemIndex] = useState<number | undefined>(undefined);

  const onClickBack = () => {
    dispatch(clearDeletedItems());
    previous && previous();
  };

  const handleClickSubmit = () => {
    onSubmit();
  };

  const getClassLabel = (ratingClassValue: string) => {
    let ratingClass = commonState.freightClasses.find(obj => obj.value === ratingClassValue);
    return ratingClass ? ratingClass.label : ratingClassValue;
  };

  useEffect(() => {
    if (!pickupState.requestFailed || pickupState.requestCreator === "CHECK_PICKUP") return;
    toast.error(pickupState.requestError || "Error", { autoClose: 4000 });
  }, [pickupState.requestFailed, pickupState.requestError, pickupState.requestCreator]);

  useEffect(() => {
    return () => {
      dispatch(clearRequestError());
    };
  }, [dispatch]);

  const hasInvalidYards = pickupState.items.some(
    (item: any) =>
      !item.deleted &&
      item.packageType === "ROLL" &&
      (!item.yards || Number(item.yards) <= 0)
  );

  return (
    <div className="xgs-items">
      <div className="xgs-bol__section">Detail Information</div>
      {pickupState.items.map((item: any, index: number) => (
        <div key={index}>
          {(index === editItemIndex) && (
            <PickupItemForm
              editMode
              itemIndex={index}
              onSubmit={(data) => {
                dispatch(updateItem({ index, data }));
                setEditItemIndex(undefined);
              }}
              onCancel={() => {
                setEditItemIndex(undefined);
              }}
            />
          )}
          {(editItemIndex === undefined || (index !== editItemIndex)) && !item.deleted && (
            <FreightItemCard
              item={item}
              onAdd={() => {
                setAddItemForm(false);
                setCopyItemIndex(undefined);
                setEditItemIndex(index);
              }}
              onRemove={() => {
                setRemoveItemIndex(index);
                setShowRemoveConfirmation(true);
              }}
              onCopy={() => {
                setCopyItemIndex(index);
                setAddItemForm(true);
              }}
              disabledCopy={pickupState.items.filter(item => !item.deleted).length >= 15}
              classLabel={getClassLabel(item.ratingClass)}
            />
          )}
        </div>
      ))}
      {(showAddItemForm || pickupState.items.length === 0) && (
        <PickupItemForm
          itemIndex={copyItemIndex}
          onSubmit={(data) => {
            dispatch(addItem(data));
            setAddItemForm(false);
            setCopyItemIndex(undefined);
          }}
        />
      )}
      {!showAddItemForm &&
        (editItemIndex === undefined) &&
        (pickupState.items.length > 0) &&
        (pickupState.items.filter((item: any) => !item.deleted).length < 15) && (
          <Button
            className="xgs-items__add"
            theme={ButtonThemes.transparent}
            size={ButtonSizes.auto}
            onClick={() => setAddItemForm(true)}
          >
            + Add Handling Unit
          </Button>
        )}
      <div className="xgs-bol__items__limitation">
        {pickupState.items.filter((item: any) => !item.deleted).length === 0
          ? (
            <span>Up to 15 units are allowed.</span>
          )
          : (
            <span>{pickupState.items.filter((item: any) => !item.deleted).length} of 15 allowed units have been entered.</span>
          )
        }
      </div>
      <div className="xgs-pickup__buttons">
        <Button
          type="button"
          theme={ButtonThemes.gray}
          onClick={onClickBack}
        >
          Back
        </Button>
        <Button
          type="button"
          theme={ButtonThemes.blue}
          disabled={
            pickupState.items.length === 0
            || pickupState.items.filter((item: any) => !item.deleted).length === 0
            || (editItemIndex !== undefined)
            || (pickupState.requestSucceed && pickupState.requestCreator === "CREATE_PICKUP")
            || hasInvalidYards
          }
          spinner={pickupState.requestStarted && pickupState.requestCreator === "CREATE_PICKUP"}
          onClick={() => setShowSubmitConfirmation(true)}
        >
          Submit
        </Button>
      </div>
      <ConfirmationModal
        fitContent
        opened={showRemoveConfirmation}
        confirmButtonText="Remove"
        onCancel={() => {
          setShowRemoveConfirmation(false);
        }}
        onConfirm={() => {
          dispatch(deleteItem(removeItemIndex));
          setShowRemoveConfirmation(false);
          setRemoveItemIndex(undefined);
        }}>
        <div className="text-center">The item will be removed from the list.</div>
      </ConfirmationModal>
      <ConfirmationModal
        fitContent
        header={<h4>Pickup Confirmation</h4>}
        opened={showSubmitConfirmation}
        confirmButtonText="Confirm"
        disableConfirmButton={!checkConfirm || loadingOnSubmit}
        spinner={loadingOnSubmit}
        onConfirm={handleClickSubmit}
        onCancel={() => {
          setShowSubmitConfirmation(false);
        }}>
        <div className="text-justify">
          <p>By confirming below, you acknowledge that you have reviewed the extracted Bill of Lading (BOL) data and the pickup details. You confirm that all the information is accurate and complete. From this point onward, any issues with the shipment arising from incorrect or missing data will be the responsibility of the employee.</p>
          <XGSCheckbox
            name="hazMat"
            onChange={() => setCheckConfirm(!checkConfirm)}
            checked={checkConfirm}
          >
            I have reviewed the BOL data and pickup details, and I confirm that the information is accurate.
          </XGSCheckbox>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default PickupItemsStep;